import { StationLeakTriggerConfigs } from '@willie/schemas'
import { Button, Flex, Switch, Text, TextField } from '@radix-ui/themes'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import * as Collapsible from '@radix-ui/react-collapsible'
import { LuChevronRight, LuPercent } from 'react-icons/lu'
import cn from 'classnames'
import OrgMemberPicker from '~/features/orgnisations/components/OrgMemberPicker'

type LeakTriggerConfigFormProps = {
  triggerKey: keyof StationLeakTriggerConfigs
  onTopicSubscriberChange: (selectedMembers: string[]) => void
  subscribedMembers: string[]
}

export const LeakTriggerConfigForm = ({
  triggerKey,
  onTopicSubscriberChange: onMemberSelectionChange,
  subscribedMembers
}: LeakTriggerConfigFormProps) => {
  const { t } = useTranslation('sites')

  const { control, watch, register } = useFormContext()

  const disabled = !watch(`${triggerKey}.enabled`)
  const [open, setOpen] = useState(false)

  return (
    <Collapsible.Root open={open} onOpenChange={setOpen}>
      <Flex direction="column" gap="7" py="3">
        <Flex justify="between" align="center" pr="6">
          <Flex direction="column" gap="1">
            <Text size="3" weight="medium">
              {t(`stations.leakTriggers.${triggerKey}.title`)}
            </Text>
            <Text size="2" color="gray">
              {t(`stations.leakTriggers.${triggerKey}.description`)}
            </Text>
          </Flex>
          <Flex align={'center'} gap={'2'}>
            <Text size={'2'} as="label" weight={'medium'}>
              {t(`stations.leakTriggers.${disabled ? 'disabled' : 'enabled'}`)}
            </Text>
            <Controller
              control={control}
              name={`${triggerKey}.enabled`}
              render={({ field }) => (
                <Switch size={'3'} checked={field.value} onCheckedChange={(checked) => field.onChange(checked)} />
              )}
            />
            <Collapsible.Trigger>
              <Button
                type="button"
                variant="ghost"
                size={'3'}
                ml="3"
                color="gray"
                className="bg-transparent cursor-pointer"
              >
                <LuChevronRight size={25} className={cn('transition-all', { ['rotate-90']: open })} />
              </Button>
            </Collapsible.Trigger>
          </Flex>
        </Flex>

        <Collapsible.Content>
          <Flex gap={'5'} justify={'between'} className={disabled ? '!text-gray-9' : ''}>
            <Flex gap="4" direction={'column'} width={'45%'}>
              <Flex gap="1" direction="column">
                <Text as="label" htmlFor={`${triggerKey}-volume-threshold`} size="2" weight="medium">
                  {t('stations.leakTriggers.volumeThreshold')}
                </Text>

                <Text size="2" color="gray">
                  {t(`stations.leakTriggers.${triggerKey}.volumeThresholdDescription`)}
                </Text>
                <TextField.Root
                  disabled={disabled}
                  id={`${triggerKey}-volume-threshold`}
                  type="number"
                  placeholder={t('stations.leakTriggers.volumeThresholdPlaceholder')}
                  min={0}
                  {...register(`${triggerKey}.volumeThreshold`, { valueAsNumber: true })}
                >
                  <TextField.Slot side="right">
                    <Text size="2" className="text-gray-11">
                      m<sup>3</sup>
                    </Text>
                  </TextField.Slot>
                </TextField.Root>
              </Flex>

              <Flex gap="1" direction="column">
                <Text as="label" htmlFor={`${triggerKey}-percent-threshold`} size="2" weight="medium">
                  {t('stations.leakTriggers.percentThreshold')}
                </Text>
                <Text size="2" color="gray">
                  {t(`stations.leakTriggers.${triggerKey}.percentThresholdDescription`)}
                </Text>
                <Controller
                  control={control}
                  name={`${triggerKey}.percentThreshold`}
                  render={({ field }) => (
                    <TextField.Root
                      disabled={disabled}
                      id={`${triggerKey}-percent-threshold`}
                      type="number"
                      placeholder={t('stations.leakTriggers.percentThresholdPlaceholder')}
                      value={field.value !== undefined ? Math.round(field.value * 100) : ''}
                      min={0}
                      max={100}
                      step={1}
                      onChange={(e) => {
                        const value = e.target.valueAsNumber / 100
                        isNaN(value) ? field.onChange(undefined) : field.onChange(value)
                      }}
                    >
                      <TextField.Slot side="right">
                        <LuPercent className="text-gray-11" />
                      </TextField.Slot>
                    </TextField.Root>
                  )}
                />
              </Flex>
            </Flex>
            <Flex direction="column" width={'48%'} gap="1">
              <Text size={'2'} weight={'medium'}>
                {t('stations.leakTriggers.notifications.title')}
              </Text>
              <Text size={'1'} color="gray">
                {t('stations.leakTriggers.notifications.description')}
              </Text>

              <OrgMemberPicker
                initialSelection={subscribedMembers}
                onSelectionChange={onMemberSelectionChange}
                disabled={disabled}
              />
            </Flex>
          </Flex>
        </Collapsible.Content>
      </Flex>
    </Collapsible.Root>
  )
}
